<template>
    <DataTable ref="ownerTable" :value="owners" :lazy="true" :paginator="true"
               v-model:rows="searchCriteria.page.limit" v-model:first="searchCriteria.page.offset" removableSort
               :totalRecords="totalRecords" :loading="loading" @page="onPage"
               @sort="onPage" :rowsPerPageOptions="[5,10,20,50]" class="owner-table" scrollable
               filterDisplay="row" v-model:filters="searchCriteria"
               scrollHeight="calc(100vh - 300px)" @row-click="onRowSelect"
               selection-mode="single" v-model:selection="selectedTableElement" >
        <Column field="id" :header="$t('message.owner.id')" :sortable="true" filterField="id"
                :showFilterMenu="false" :showClearButton="false" class="w-1">
            <template #filter>
                <CustomInputNumber name="id" v-model="searchCriteria.id.value" class="mb-0"
                                   @update:modelValue="search"/>
            </template>
        </Column>
        <Column field="suffix" :header="$t('message.owner.suffix')" :sortable="true" filterField="suffix"
                :showFilterMenu="false" :showClearButton="false" class="w-1">
            <template #filter>
                <CustomInputText name="suffix" class="mb-0" v-model="searchCriteria.suffix.value"
                                 @update:modelValue="search" />
            </template>
        </Column>
        <Column field="fullName" :header="$t('message.owner.fullName')" :sortable="true" filterField="fullName"
                :showFilterMenu="false" :showClearButton="false" class="w-1">
            <template #filter>
                <CustomInputText name="fullName" class="mb-0" v-model="searchCriteria.fullName.value"
                                 @update:modelValue="search" />
            </template>
        </Column>
        <Column field="owner" :header="$t('message.owner.isOwner')" :sortable="false" filterField="isOwner"
                :showFilterMenu="false" :showClearButton="false" class="w-1 text-center">
            <template #body="{ data }">
                <i v-if="data.owner" class="pi pi-check"/>
                <i v-else class="pi pi-times"/>
            </template>
            <template #filter>
                <CustomSelectOneYesNo name="isOwner" v-model="searchCriteria.isOwner.value" class="mb-0"
                                      @update:modelValue="search" />
            </template>
        </Column>
        <Column field="breeder" :header="$t('message.owner.isBreeder')" :sortable="false" filterField="isBreeder"
                :showFilterMenu="false" :showClearButton="false" class="w-1 text-center">
            <template #body="{ data }">
                <i v-if="data.breeder" class="pi pi-check"/>
                <i v-else class="pi pi-times"/>
            </template>
            <template #filter>
                <CustomSelectOneYesNo name="isBreeder" v-model="searchCriteria.isBreeder.value" class="mb-0"
                                      @update:modelValue="search"/>
            </template>
        </Column>
        <Column field="address.country.alpha3" :header="$t('message.owner.address.country')" :sortable="true" filterField="countryId"
            :showFilterMenu="false" :showClearButton="false" class="w-1">
            <template #filter>
                <CustomSelectOne name="country" :items="countries" itemValue="id" itemLabel="name"
                    v-model="searchCriteria.countryId.value" @update:modelValue="search" />
            </template>
        </Column>
        <Column field="address.region" :header="$t('message.owner.address.region')"
            :sortable="true" filterField="region"
            :showFilterMenu="false" :showClearButton="false" class="w-1">
            <template #filter>
                <CustomInputText name="region" class="mb-0" v-model="searchCriteria.region.value"
                                 @update:modelValue="search" />
            </template>
        </Column>
        <Column field="address.postalCode" :header="$t('message.owner.address.postalCode')" :sortable="true" filterField="postalCode"
            :showFilterMenu="false" :showClearButton="false" class="w-1">
            <template #filter>
                <CustomInputText name="postalCode" class="mb-0" v-model="searchCriteria.postalCode.value"
                                 @update:modelValue="search" />
            </template>
        </Column>
        <Column field="address.city" :header="$t('message.owner.address.city')" :sortable="true" filterField="city"
                :showFilterMenu="false" :showClearButton="false" class="w-1">
            <template #filter>
                <CustomInputText name="city" class="mb-0" v-model="searchCriteria.city.value"
                                 @update:modelValue="search" />
            </template>
        </Column>
        <Column field="address.street" :header="$t('message.owner.address.street')" :sortable="true" filterField="street"
                :showFilterMenu="false" :showClearButton="false" class="w-2">
            <template #filter>
                <CustomInputText name="street" class="mb-0" v-model="searchCriteria.street.value"
                                 @update:modelValue="search" />
            </template>
        </Column>
        <Column field="id" :header="$t('message.owner.history')" class="w-1">
            <template #body="{ data }">
                <CustomIconButton icon="pi pi-download font-bold" onlyIcon @click="() => downloadReport(data.id)" class="close-form-button" />
            </template>
        </Column>
        <template #empty>
            {{ $t('message.other.emptyTable') }}
        </template>
    </DataTable>
</template>

<script>
    import DataTable from "primevue/datatable";
    import Column from "primevue/column";
    import {
        searchOwner,
        searchOwnerCount,
    } from "@/swagger/vue-api-client";
    import CustomButton from "@/components/form/CustomButton.vue";
    import { ScrollToUtils } from "@/utils/ScrollToUtils";
    import CustomInputText from "@/components/form/CustomInputText.vue";
    import CustomSelectOne from "@/components/form/inner/CustomSelectOne.vue";
    import CustomSelectOneYesNo from "@/components/form/CustomSelectOneYesNo.vue";
    import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
    import {FileUtils} from "@/utils/FileUtils";
    import CustomIconButton from "@/components/form/CustomIconButton.vue";
    import {FilterMatchMode} from "primevue/api";
    import {SearchCriteria} from "@/utils/SearchCriteria";

    export default {
        name: "OwnerTable",
        components: {
            CustomIconButton,
            CustomButton,
            Column,
            DataTable,
            CustomInputText,
            CustomSelectOne,
            CustomSelectOneYesNo,
            CustomInputNumber,
        },

        props: {
            modelValue: null,
            countries: {
                type: Array,
                required: true,
            },
        },

        emits: ["update:modelValue", "showForm"],

        data() {
            return {
                loading: false,
                totalRecords: 0,
                owners: [],
                searchCriteria: this.getClearSearchCriteria(),
                selectedTableElement: null,
            };
        },

        mounted() {
            this.updateTotalRecords();
            this.onPage(this.getFirstPage());
        },

        methods: {
            search() {
                this.onPage(this.getFirstPage());
                this.updateTotalRecords();
            },

            onPage(event) {
                this.loading = true;

                this.searchCriteria.page.offset = event.first;
                this.searchCriteria.page.limit = event.rows;
                this.searchCriteria.page.sortField = event.sortField;
                this.searchCriteria.page.sortOrder = event.sortOrder;

                const body = SearchCriteria.prepareCriteria(this.searchCriteria);
                searchOwner({body}).then((response) => {
                    this.owners = response.data;
                    this.loading = false;
                });
            },

            updateTotalRecords() {
                const body = SearchCriteria.prepareCriteria(this.searchCriteria);
                searchOwnerCount({body}).then((response) => {
                    this.totalRecords = response.data;
                });
            },

            getFirstPage() {
                return {
                    first: this.searchCriteria.page.offset,
                    rows: this.searchCriteria.page.limit,
                };
            },

            onRowSelect(event) {
                this.$emit("showForm");
                this.selectedOwner = event.data;
                ScrollToUtils.scrollToElement(true);
            },

            getClearSearchCriteria() {
                return {
                    id: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
                    suffix: { value: null, matchMode: FilterMatchMode.CONTAINS },
                    fullName: { value: null, matchMode: FilterMatchMode.CONTAINS },
                    street: { value: null, matchMode: FilterMatchMode.CONTAINS },
                    city: { value: null, matchMode: FilterMatchMode.IN },
                    postalCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
                    region: { value: null, matchMode: FilterMatchMode.CONTAINS },
                    countryId: { value: null, matchMode: FilterMatchMode.IN },
                    isBreeder: { value: null, matchMode: FilterMatchMode.EQUALS },
                    isOwner: { value: null, matchMode: FilterMatchMode.EQUALS },
                    page: {
                        limit: 10,
                        offset: 0,
                        sortField: null,
                        sortOrder: null,
                    },
                };
            },

            addNewOwner() {
                this.selectedTableElement = null;
            },

            reloadTable() {
                this.search();
            },
            downloadReport(ownerId) {
                // eslint-disable-next-line max-len
                FileUtils.downloadFileUsingGET("ownerReport.pdf", "/report/owner/" + ownerId);
            },
        },

        computed: {
            selectedOwner: {
                get() { return this.modelValue; },
                set(value) { this.$emit("update:modelValue", value); },
            },
        },

        // watch: {
        //     searchCriteria: {
        //         handler() {
        //             this.search();
        //         },
        //         deep: true,
        //     },
        // },
    };
</script>

<style lang="less" >
    .owner-table {
        .custom-select-one {
            margin-bottom: 0;
        }
    }
</style>
